import * as FC from "@fluentui/react-components";
import * as FDC from "@fluentui/react-datepicker-compat";
import * as IC from "@fluentui/react-icons";
import { useCommonStyles } from "../../style_util";
import {
    TaskPriorityBadge,
    TaskPriorityDropdown,
    TaskStatusBadge,
    TaskStatusDropdown,
    idString,
} from "../../db_utils";
import { UserDropdown } from "../../services/user_service";
import { FieldItemDropdown } from "../../services/field_item_service";
import { bindDropdownValue, bindInputValue } from "../../form_utils";
import { useContext, useEffect, useState } from "react";
import { SimpleGrid } from "../../components/simple_grid";
import { TaskDef } from "../../services/task_service";
import { useNavigate } from "react-router-dom";
import { Context } from "../../context";
import { toDateString, yyyymmdd } from "../../date_utils";
import { getCurrentFieldIdFromNew } from "../../workspace";

const listTask = async (apiClient, filters) => {
    try {
        const params = {};
        
        // Map filters to API parameters
        if (filters) {
            if (filters.seq) params.search = filters.seq;
            if (filters.name) params.search = filters.name;
            if (filters.priority) params.priority = filters.priority;
            if (filters.status) params.status = filters.status;
            if (filters.owner_checker) params.checkerId = filters.owner_checker;
            if (filters.owner_creater) params.createrId = filters.owner_creater;
            // Note: Date range filtering not yet supported in API
        }
        
        // Get current field from workspace context
        let fieldId = getCurrentFieldIdFromNew();
        if (fieldId) {
            // Extract numeric ID if it's in SurrealDB format (e.g., "field_item:1" -> "1")
            if (typeof fieldId === 'string' && fieldId.includes(':')) {
                fieldId = fieldId.split(':')[1];
            }
            params.fieldId = fieldId;
        }
        
        const tasks = await apiClient.getTasks(params);
        const items = (tasks || []);
        // Session‑centric view: show only root tasks (session roots)
        const roots = items.filter(t => {
            const isRoot = (t?.seq?.endsWith('-00') || (t?.name || '').endsWith(' - ROOT')) && t.sessionId;
            return isRoot;
        });
        return roots;
    } catch (error) {
        console.error("Failed to load datacenter tasks:", error);
        return [];
    }
};

export const DatacenterTaskManagement = () => {
    const { apiClient } = useContext(Context);
    const [filters, setFilters] = useState({});
    const [items, setItems] = useState([]);
    const loadItems = async () => {
        let items = await listTask(apiClient, filters);
        setItems(items);
    };
    useEffect(() => {
        if (!apiClient) {
            return;
        }
        loadItems();
    }, [apiClient, filters]);

    const nav = useNavigate();
    let v = bindInputValue(null, filters, setFilters);
    let d = bindDropdownValue(null, filters, setFilters);

    const onSearch = async () => {
        loadItems();
    };
    const columns = [
        TaskDef.createTableColumn("seq"),
        TaskDef.createTableColumn("name", {
            renderCell: (item) => {
                return <FC.TableCell>{item?.name}</FC.TableCell>;
            },
        }),
        TaskDef.createTableColumn("field_item", {
            renderCell: (item) => {
                return (
                    <FC.TableCellLayout>
                        <div className="">
                            {TaskDef.fields.field_item.display(item)}
                        </div>
                    </FC.TableCellLayout>
                );
            },
        }),
        TaskDef.createTableColumn("priority", {
            renderCell: (item) => {
                return <TaskPriorityBadge priority={item.priority} />;
            },
        }),
        TaskDef.createTableColumn("status", {
            renderCell: (item) => {
                // Display session status for root tasks to reflect partial/complete progress
                const status = item?.session?.status || item.status;
                return <TaskStatusBadge status={status} />;
            },
        }),
        FC.createTableColumn({
            columnId: 'progress',
            renderHeaderCell: () => <FC.TableCell>进度</FC.TableCell>,
            renderCell: (item) => {
                const total = item?.session?.totalTurbines ?? 0;
                const done = item?.session?.completedTurbines ?? 0;
                return (
                    <FC.TableCellLayout>
                        <span>{done}/{total}</span>
                    </FC.TableCellLayout>
                );
            }
        }),
        TaskDef.createTableColumn("owner_checker"),
        TaskDef.createTableColumn("owner_creater"),
        TaskDef.createTableColumn("created_at"),
        TaskDef.createTableColumn("completed_at"),
        TaskDef.createOpsColumn({
            renderCell: (item) => {
                const onClick = (evt) => {
                    console.log(evt);
                    evt.preventDefault();
                    let url = `/datacenter/viewtask/${idString(item.id)}`;
                    nav(url);
                };
                return (
                    <FC.TableCellLayout>
                        <FC.Link
                            href={`/datacenter/viewtask/${idString(item.id)}`}
                            onClick={onClick}
                        >
                            查看详情
                        </FC.Link>
                    </FC.TableCellLayout>
                );
            },
        }),
    ];

    const commonStyles = useCommonStyles();
    return (
        <div className={commonStyles.root}>
            <div className="grid grid-cols-4 gap-4">
                <FC.Field label="任务编号">
                    <FC.Input {...v("seq")} />
                </FC.Field>
                <FC.Field label="任务名称">
                    <FC.Input {...v("name")} />
                </FC.Field>
                <FC.Field label="优先级">
                    <TaskPriorityDropdown {...d("priority")} />
                </FC.Field>
                <FC.Field label="状态">
                    <TaskStatusDropdown {...d("status")} />
                </FC.Field>
                <FC.Field label="当前负责人">
                    <UserDropdown {...d("owner_checker")} />
                </FC.Field>
                <FC.Field label="创建人">
                    <UserDropdown {...d("owner_creater")} />
                </FC.Field>
                <FC.Field
                    label="巡检时间"
                    className="col-span-2 whitespace-nowrap"
                >
                    <div className="flex flex-row gap-2 items-center">
                        <FDC.DatePicker
                            formatDate={toDateString}
                            onSelectDate={(date) => {
                                setFilters({
                                    ...filters,
                                    completed_date_from: yyyymmdd(date),
                                });
                            }}
                        />{" "}
                        -{" "}
                        <FDC.DatePicker
                            formatDate={toDateString}
                            onSelectDate={(date) => {
                                setFilters({
                                    ...filters,
                                    completed_date_to: yyyymmdd(date),
                                });
                            }}
                        />
                    </div>
                </FC.Field>
            </div>
            <SimpleGrid items={items} columns={columns} />
        </div>
    );
};
